import { debounce, breakpoint } from '../../util/helpers';
import { setElementInert, setParentInert } from '../../util/a11y/handle-inert';
import strings from '../../lang/strings.json';

export default {
  init() {
    const ingredientsList = document.querySelector('.ms-ingredients-list');
    const recipeSteps = document.querySelector('.ms-recipe-steps');

    if (ingredientsList && recipeSteps) {
      this.startListener(ingredientsList, recipeSteps);
    }
  },
  startListener(ingredientsList, recipeSteps) {
    const selector = {
      button: '.ms-ingredients-list__tab-button',
      heading: '.ms-recipe-steps__heading',
      ingredientsColumnTab: '.ms-ingredients-list__column-tabs',
      ingredientsRow: '.ms-ingredients-list__row',
      ingredientsTab: '.ms-ingredients-list__tab',
      miniListModule: '.ms-ingredients-mini-list',
      miniListContent: '.ms-ingredients-mini-list__content',
      miniListInner: '.ms-ingredients-mini-list__inner',
      miniListItem: '.ms-ingredients-mini-list__item',
      miniListList: '.ms-ingredients-mini-list__list',
      miniListTitle: '.ms-ingredients-mini-list__title',
      miniListSkipButton: '.ms-ingredients-mini-list__sr-next-recipe-button',
      recipeSection: '.ms-recipe-steps__section',
      recipeOffset: '.ms-recipe-steps__offset',
      recipeStepTitle: '.ms-recipe-steps__recipe-title',
      recipeSkipButton: '.ms-recipe-steps__sr-skip-button',
      moduleTitle: '.ms-recipe-steps__title',
    };

    const buttons = ingredientsList.querySelectorAll(selector.button);
    const ingredientsTabs = ingredientsList.querySelectorAll(selector.ingredientsTab);
    const miniListContent = recipeSteps.querySelector(selector.miniListContent);
    const miniListInner = recipeSteps.querySelector(selector.miniListInner);
    const miniListTitle = recipeSteps.querySelector(selector.miniListTitle);
    const recipeHeading = recipeSteps.querySelector(selector.heading);
    const moduleTitle = recipeSteps.querySelector(selector.moduleTitle);
    const miniListModule = recipeSteps.querySelector(selector.miniListModule);
    const recipeOffsets = recipeSteps.querySelectorAll(selector.recipeOffset);
    const recipeStepTitles = recipeSteps.querySelectorAll(selector.recipeStepTitle);
    const skipToIngredientButtons = recipeSteps.querySelectorAll(selector.recipeSkipButton);
    const skipToRecipeButton = recipeSteps.querySelector(selector.miniListSkipButton);
    const { dataset: { hasIngredientsLabel, noIngredientsLabel } } = miniListTitle;

    const lang = document.querySelector('html').lang;

    const isSafari = navigator.userAgent.match(/Version\/[\d.]+.*Safari/);
    if (isSafari) {
      miniListInner.classList.add('ms-ingredients-mini-list__inner--safari');
    }

    /* Check if in mobile breakpoint */
    const isMobile = () => {
      const screenSize = parseInt(getComputedStyle(document.querySelector('html')).width);
      const mobile = screenSize < 768;

      return mobile;
    }

    // set the module always below the title
    const handlePosition = () => {
      if (breakpoint() === 'mobile') { return; }

      if (moduleTitle && miniListModule) {
        const margin = breakpoint() === 'tablet' ? 33 : 40;
        miniListModule.style.marginTop = `${moduleTitle.getBoundingClientRect().height + margin}px`; // 33 margin-bottom
      }
    }

    /* Handle the display of the content based on the active measurement in the Ingredients List module */
    const handleDisplay = debounce(target => {
      ingredientsTabs.forEach(tab => {
        const { unit } = tab.dataset;

        if (target.toLowerCase() === unit.toLowerCase()) {
          const rows = tab.querySelectorAll(selector.ingredientsRow);
          const lists = recipeSteps.querySelectorAll(selector.miniListList);

          if (lists.length > 0) {
            lists.forEach(list => list.remove());
          }

          rows.forEach((row, index) => {
            const tab = row.querySelector(selector.ingredientsColumnTab);
            const items = tab.querySelectorAll('ul li');
            const listElement = document.createElement('ul');

            listElement.setAttribute('role', 'list');
            listElement.setAttribute('aria-label', strings[lang].ingredientsList);

            if (index === 0) {
              listElement.classList.add('ms-ingredients-mini-list__list', 'active');
            } else {
              listElement.classList.add('ms-ingredients-mini-list__list');
            }

            items.forEach(item => {
              const itemElement = document.createElement('li');
              itemElement.classList.add('ms-ingredients-mini-list__item');
              itemElement.innerHTML = item.innerHTML;
              miniListContent.appendChild(listElement);
              listElement.appendChild(itemElement);
            });
          });
        }
      });
    }, 50);

    /* Handle inert */
    const handleInert = debounce(() => {
      const activeList = miniListContent.querySelector(`${selector.miniListList}.active`);

      if (miniListInner.classList.contains('active')) {
        setParentInert(true, activeList);
      } else {
        setParentInert(true, miniListContent);
      }

      if (isMobile() === true) {
        setElementInert(false, miniListTitle);
        setElementInert(true, miniListContent);
        setElementInert(true, skipToRecipeButton);
      } else {
        setElementInert(true, miniListTitle);
        setElementInert(false, miniListContent);
        setElementInert(false, activeList);
        setElementInert(false, skipToRecipeButton);
      }
    }, 50);

    /* Initiate handleDisplay function */
    handleDisplay('imperial');

    buttons.forEach(button => button.addEventListener('click', e => handleDisplay(e.target.dataset.target)));

    /* Resize the mini-list size depending on the amount of content */
    const resizeMiniList = () => {
      const miniListStyle = miniListContent && getComputedStyle(miniListContent);
      const miniListTitleStyle = getComputedStyle(miniListTitle);
      const miniListHeight = miniListStyle &&
        miniListContent.clientHeight +
        parseInt(miniListStyle.marginBottom) +
        parseInt(miniListStyle.marginTop) +
        parseInt(miniListTitleStyle.height) +
        parseInt(miniListTitleStyle.marginBottom) +
        parseInt(miniListTitleStyle.marginTop);

      const hasActiveContent = miniListContent.querySelector('ul.active');

      // switch label if no active content
      miniListTitle.innerHTML = hasActiveContent ? hasIngredientsLabel : noIngredientsLabel;

      miniListInner.style.height = `${miniListHeight}px`;

      if (isMobile() === true) {
        miniListTitle.style.width = `${miniListHeight}px`;
      } else {
        miniListTitle.removeAttribute('style');
        miniListInner.classList.remove('active');
      }
    };

    /* Initiate functions on load */
    window.addEventListener('load', () => {
      resizeMiniList();
      handleInert();
      handlePosition();
    });

    /* Initiate functions on resize */
    window.addEventListener('resize', () => {
      resizeMiniList();
      handleInert();
      handlePosition();
    });

    /* Close the mini-list upon clicking outside */
    document.addEventListener('click', ({ target}) => {
      if (isMobile() === true) {
        if (target.closest(`${selector.miniListInner}`)) {
          return;
        } else {
          setParentInert(true, miniListContent);
          miniListInner.classList.remove('active');
          miniListTitle.setAttribute('aria-expanded', false);
        }
      }
    });

    /* Toggle for the mobile mini-list display */
    const toggleMobileMiniListDisplay = () => {
      const activeList = miniListContent.querySelector(`${selector.miniListList}.active`);

      miniListInner.classList.toggle('active');

      if (miniListInner.classList.contains('active')) {
        setElementInert(false, [activeList, miniListContent]);
        setElementInert(false, skipToRecipeButton);
        miniListTitle.setAttribute('aria-expanded', true);

        if (skipToRecipeButton.getAttribute('data-recipe-index') === null) {
          setElementInert(true, skipToRecipeButton);
        }
      } else {
        setElementInert(true, [activeList, miniListContent]);
        setElementInert(true, skipToRecipeButton);
        miniListTitle.setAttribute('aria-expanded', false);
      }
    };

    miniListTitle.addEventListener('click', () => {
      toggleMobileMiniListDisplay();
    });

    recipeStepTitles.forEach(recipeTitle => {
      if (isMobile()) {
        recipeTitle.addEventListener('focus', () => {
          // Close the mini-list the the focus shifted to the recipe title.
          if (miniListInner.classList.contains('active')) {
            setTimeout(() => {
              toggleMobileMiniListDisplay();
            }, 1000);
          }
        });
      }
    });

    /* Setup event handlers for skip buttons */
    skipToIngredientButtons.forEach(skipButton => {
      skipButton.addEventListener('click', () => {
        // For mobi8le, just focus on the mini-list toggle button instead of the content.
        if (isMobile()) {
          miniListTitle.focus();
        }
        else {
          miniListContent.setAttribute('tabindex', -1);

          setTimeout(() => {
            miniListContent.focus();
            miniListContent.removeAttribute('tabindex');
          }, 1000);
        }
      });
    });

    skipToRecipeButton.addEventListener('click', ({ target }) => {
      const { recipeIndex } = target.dataset;

      if (recipeIndex) {
        const targetRecipeTitle = document.getElementById(`recipe-${recipeIndex}`);

        targetRecipeTitle.setAttribute('tabindex', -1);

        setTimeout(() => {
          targetRecipeTitle.focus();
          targetRecipeTitle.removeAttribute('tabindex');
        }, 1000);
      }
    });

    /* Watch & set the active list upon scrolling */
    const options = {
      root: null,
      rootMargin: '-10% 0px -80%',
      threshold: 0.1,
    }

    const listOptions = {
      root: null,
      rootMargin: '0px 0px -80%',
      delay: 100,
      threshold: breakpoint() === 'mobile' ? [0, 0.4] : [0, 0.8],
    }

    const watchHeading = entries => {
      entries.forEach(({ isIntersecting }) => {
        if (isIntersecting && miniListInner.classList.contains('hidden')) {
          miniListInner.classList.remove('hidden');
        }
      });
    }

    let lastActive = null;
    const watchLists = entries => {
      entries.forEach(({ isIntersecting, target }) => {
        let parent = target.parentElement;
        let active = parseInt(parent.dataset.index);
        if(isIntersecting) {
          updateActive(target);
        } else if( lastActive === active ) {
          parent = parent.previousElementSibling;
          if(parent) {
            target = parent.querySelector(selector.recipeOffset);
            updateActive(target);
          }
        }
      });
    }

    const updateActive = (target) => {
      const miniListLists = recipeSteps.querySelectorAll(selector.miniListList);
      const parent = target.parentElement;
      const previousActive = recipeSteps.querySelector(`${selector.miniListList}.active`);
      let active = parseInt(parent.dataset.index);

      if (miniListLists[active] !== previousActive) {
        if (previousActive) {
          previousActive.classList.remove('active');

          setElementInert(true, previousActive);
        } else if(!miniListLists[active]) {
          if(recipeSteps.querySelector(`${selector.miniListList}.active`)) {
            setTimeout(() => {
              recipeSteps.querySelector(`${selector.miniListList}.active`).classList.remove('active');
              resizeMiniList();
            }, 500);
          }
        }

        if (miniListLists[active]) {
          miniListLists[active].classList.add('active');
          if (miniListInner.classList.contains('active') || isMobile() === false) {
            setElementInert(false, miniListLists[active]);
          }

          skipToRecipeButton.setAttribute('data-recipe-index', active + 1);
        }
      }

      if (miniListInner.classList.contains('hidden')) {
        miniListInner.classList.remove('hidden');
      }

      // If the index is of the last list, hide the min-list skip button.
      if (active === miniListLists.length - 1) {
        skipToRecipeButton.removeAttribute('data-recipe-index');
      }

      if (!isMobile()) {
        setElementInert(active === miniListLists.length - 1, skipToRecipeButton);
      }
      lastActive = active;
      resizeMiniList();
    }

    const headingObserver = new IntersectionObserver(watchHeading, options);
    const listsObserver = new IntersectionObserver(watchLists, listOptions);
    const recipeSectionsArray = Array.prototype.map.call(recipeOffsets, sectionElem => sectionElem);

    headingObserver.observe(recipeHeading);

    [...recipeSectionsArray].forEach(recipeElement => {
      listsObserver.observe(recipeElement);
    });
  },
}



// WEBPACK FOOTER //
// /private/var/www/Morton-Windsor/wp-content/themes/morton-salt/resources/assets/scripts/multisite/modules/ingredients-mini-list.js